import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';

type SystemId = {
  description?: string;
  'part number'?: string;
  sn?: string;
  'mgmt mac'?: string;
  'sw version'?: string;
  configname?: string;
};

type SystemInfo = {
  name?: string;
  description?: string;
  location?: string;
  contact?: string;
};

type SystemSlots = {
  booted?: 'system0' | 'system1';
  system0?: string;
  system1?: string;
};

type UnsavedChanges = { changes?: boolean };
type FilesResponse = {
  files?: Array<{ name: string; size: number; url?: string }>;
};

type Snapshot = {
  id: SystemId;
  info: SystemInfo;
  slots: SystemSlots;
  unsaved: UnsavedChanges;
  files: FilesResponse;
};

function formatSize(size: number): string {
  if (size >= 1024 * 1024) return `${Math.floor(size / (1024 * 1024))} MB`;
  if (size >= 1024) return `${Math.floor(size / 1024)} KB`;
  return `${size} B`;
}

export default function WebUiStatusSystemPage() {
  const [data, setData] = React.useState<Snapshot | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const id = await postWebUiApi<SystemId>({
          service: 'system',
          access: 'get',
          command: 'get id open api',
        }).catch(() =>
          postWebUiApi<SystemId>({
            service: 'system',
            access: 'get',
            command: 'get id',
          }),
        );

        const [info, slots, unsaved, files] = await Promise.all([
          postWebUiApi<SystemInfo>({
            service: 'system',
            access: 'get',
            command: 'get info',
          }),
          postWebUiApi<SystemSlots>({
            service: 'system',
            access: 'get',
            command: 'slots',
          }),
          postWebUiApi<UnsavedChanges>({
            service: 'system',
            access: 'get',
            command: 'has unsaved changes',
          }),
          postWebUiApi<FilesResponse>({
            service: 'system',
            access: 'get',
            command: 'files',
          }),
        ]);

        if (!alive) return;
        setData({ id, info, slots, unsaved, files });
        setError(null);
      } catch (err) {
        if (!alive) return;
        setError((err as Error).message);
      }
    };

    load();
    const timer = window.setInterval(() => load(), 5000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const altVersion =
    data?.slots.booted === 'system0'
      ? data?.slots.system1
      : data?.slots.system0;

  return (
    <WebUiPageWrapper titleList={['Status', 'System']}>
      {error && <WebUiAlert type="error" message={error} />}
      <div className="w-full">
        <h4 className="font-bold">System Info</h4>
        <table className="webui-table">
          <thead>
            <tr className="webui-table-row-head">
              <th className="w-1/3">Info</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Hostname</td>
              <td>{data?.info.name ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Serial number</td>
              <td>{data?.id.sn ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Management MAC</td>
              <td>{data?.id['mgmt mac'] ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Part No.</td>
              <td>{data?.id['part number'] ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Device Description</td>
              <td>{data?.id.description ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Software Version (Booted)</td>
              <td>{data?.id['sw version'] ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Software Version (Alternate)</td>
              <td>{altVersion ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Configuration Name</td>
              <td>{data?.id.configname ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Configuration Status</td>
              <td>{data?.unsaved.changes ? 'unsaved changes' : 'saved'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>User-defined Description</td>
              <td>{data?.info.description ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>User-defined Location</td>
              <td>{data?.info.location ?? '-'}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>User-defined Contact</td>
              <td>{data?.info.contact ?? '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="w-full">
        <h4 className="font-bold">Files List</h4>
        <table className="webui-table">
          <thead>
            <tr className="webui-table-row-head">
              <th>Name</th>
              <th className="w-[120px]">Size</th>
              <th>URL</th>
            </tr>
          </thead>
          <tbody>
            {(data?.files.files ?? []).map((file) => (
              <tr key={file.name} className="webui-table-row-content">
                <td className="truncate">{file.name}</td>
                <td className="text-right">{formatSize(file.size)}</td>
                <td className="truncate">{file.url ?? 'N/A'}</td>
              </tr>
            ))}
            {!data?.files.files?.length ? (
              <tr className="webui-table-row-content">
                <td colSpan={3} className="text-center">
                  No files found
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </WebUiPageWrapper>
  );
}
